import { createSelector } from 'reselect';
import { get, find, sumBy } from 'lodash';
import { enrichCriteria } from 'containers/TargetingPeeqPage/utils';

/**
 * Direct selector to the TargetingPeeqPage state domain
 */
const selectTargetingPageDomain = (state) => get(state, 'targetingPage', {});
const selectAudiencesDomain = (state) =>
  get(state, 'targetingPage.audiences', []);
const selectActiveAudienceRefIdDomain = (state) =>
  get(state, 'targetingPage.activeAudienceRefId');

const makeSelectActiveAudienceRefId = createSelector(
  selectActiveAudienceRefIdDomain,
  (substate) => substate,
);
const makeSelectAudiences = createSelector(
  selectAudiencesDomain,
  (substate) => substate,
);

const makeSelectActiveAudience = createSelector(
  selectAudiencesDomain,
  selectActiveAudienceRefIdDomain,
  (audiences, audienceRefId) => find(audiences, ['refId', audienceRefId]),
);

const makeSelectActiveAudienceCriteria = createSelector(
  makeSelectActiveAudience,
  (audience) => audience?.criteria || [],
);

const makeSelectTargetingVersion = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.targetingEditVersion,
);
const makeSelectInitialTargetingVersion = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.startVersion,
);

const makeSelectHasDifferencesInAudiences = createSelector(
  selectTargetingPageDomain,
  (substate) => get(substate, 'hasDifferencesInAudiences', false),
);

const makeSelectPeeqAudienceByRefId = (refId) =>
  createSelector(selectAudiencesDomain, (audiences) =>
    find(audiences, ['refId', refId]),
  );

const makeSelectTargetingPage = createSelector(
  selectTargetingPageDomain,
  (substate) => substate,
);

const makeSelectErrors = createSelector(selectTargetingPageDomain, (substate) =>
  get(substate, 'errors', []),
);

const makeSelectAudienceErrors = createSelector(
  selectTargetingPageDomain,
  (substate) =>
    get(substate, 'errors', []).filter(
      (error) => error.category === 'audience' || error.category === 'criteria',
    ),
);

const makeSelectAudienceCompletesSum = createSelector(
  selectAudiencesDomain,
  (audiences = []) => sumBy(audiences, 'quotaCompletes'),
);

const makeSelectHasFetched = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.hasFetched,
);

const makeSelectMainCriteria = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.mainCriteria,
);

const makeSelectCustomQuestions = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.customQuestions,
);

const makeSelectCustomQuestionsKey = createSelector(
  makeSelectCustomQuestions,
  (customQuestions) => {
    const [customQuestionsKey] = Object.keys(customQuestions);

    return customQuestionsKey;
  },
);

const makeSelectIsSavingCustomQuestions = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.isSavingCustomQuestions,
);

const makeSelectAllCriteria = createSelector(
  makeSelectMainCriteria,
  makeSelectCustomQuestions,
  (mainCriteria, customQuestions) => ({
    ...mainCriteria,
    ...customQuestions,
  }),
);

const makeSelectIsFetching = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.isFetching,
);

const makeSelectIsFetchingPeeqCriteria = createSelector(
  selectTargetingPageDomain,
  (substate) =>
    substate.isFetchingPeeqCriteria || substate.isFetchingCustomQuestions,
);

const makeSelectAllCriteriaUnGrouped = createSelector(
  makeSelectAllCriteria,
  (allCriteria) =>
    Object.keys(allCriteria).reduce(
      (accumulator, key) => [...accumulator, ...allCriteria[key]],
      [],
    ),
);

const makeSelectActivePeeqCriteria = createSelector(
  makeSelectActiveAudienceCriteria,
  makeSelectAllCriteriaUnGrouped,
  (criteria, allCriteriaUnGrouped) =>
    enrichCriteria({ criteria, allCriteriaUnGrouped }),
);

const makeSelectAudiencesHasAtLeastOneAudience = createSelector(
  selectAudiencesDomain,
  (audiences = []) => audiences.length > 0,
);

const makeSelectCountryId = (audienceRefId) =>
  createSelector(selectTargetingPageDomain, (substate) => {
    // TODO RES-9859: perhaps check supportsMultiCountries flag instead of countryId location in schema

    if (substate.countryId) {
      return substate.countryId;
    }

    if (!audienceRefId) {
      const { activeAudienceRefId } = substate;
      const activeAudience = find(substate.audiences, [
        'refId',
        activeAudienceRefId,
      ]);

      return activeAudience?.countryId;
    }

    const targetAudience = find(substate.audiences, ['refId', audienceRefId]);
    if (targetAudience) {
      return targetAudience.countryId;
    }

    return undefined;
  });

const makeSelectNoAudiences = createSelector(
  makeSelectAudiences,
  (audiences = []) => audiences.length === 0,
);

const makeSelectHasActiveAudience = createSelector(
  makeSelectAudiences,
  (audiences = []) => audiences.some((audience) => audience.isActive),
);

const makeSelectModals = (name) =>
  createSelector(
    selectTargetingPageDomain,
    (substate) => substate.modals[name],
  );

const makeSelectIsSavingGroupAnswers = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.isSavingGroupAnswers,
);

const makeSelectAllAudiencesCriteria = createSelector(
  makeSelectAudiences,
  (audiences) =>
    audiences.reduce(
      (accumulator, audience) => [
        ...accumulator,
        ...audience.criteria.map((criterion) => ({
          ...criterion,
          audienceRefId: audience.refId,
        })),
      ],
      [],
    ),
);

const makeSelectAudienceNames = createSelector(
  selectAudiencesDomain,
  (audiences) =>
    audiences.map(({ externalAudienceId, quotaName = '' }) => ({
      id: externalAudienceId,
      name: quotaName,
    })),
);

const makeSelectActiveAudienceExternalAudienceId = createSelector(
  makeSelectActiveAudience,
  (activeAudience) => activeAudience?.externalAudienceId,
);

const makeSelectActiveAudienceTimestamp = createSelector(
  makeSelectActiveAudience,
  (activeAudience) => activeAudience?.timestamp,
);

const makeSelectCountries = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.countries,
);

const makeSelectShowAudienceTemplates = createSelector(
  selectTargetingPageDomain,
  (substate) => substate.showAudienceTemplates,
);

export default makeSelectTargetingPage;
export {
  selectTargetingPageDomain,
  makeSelectTargetingVersion,
  makeSelectInitialTargetingVersion,
  makeSelectErrors,
  makeSelectAudiences,
  makeSelectActiveAudience,
  makeSelectActiveAudienceRefId,
  makeSelectAudienceErrors,
  makeSelectAudienceCompletesSum,
  makeSelectHasFetched,
  makeSelectAllCriteria,
  makeSelectIsFetching,
  makeSelectIsFetchingPeeqCriteria,
  makeSelectAllCriteriaUnGrouped,
  makeSelectActivePeeqCriteria,
  makeSelectAudiencesHasAtLeastOneAudience,
  makeSelectCountryId,
  makeSelectPeeqAudienceByRefId,
  makeSelectNoAudiences,
  makeSelectCustomQuestionsKey,
  makeSelectIsSavingCustomQuestions,
  makeSelectModals,
  makeSelectHasActiveAudience,
  makeSelectIsSavingGroupAnswers,
  makeSelectAllAudiencesCriteria,
  makeSelectAudienceNames,
  makeSelectActiveAudienceExternalAudienceId,
  makeSelectActiveAudienceTimestamp,
  makeSelectCountries,
  makeSelectShowAudienceTemplates,
  makeSelectHasDifferencesInAudiences,
};
